import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { RiCalendarLine } from "@remixicon/react";
import { tokens } from "../../utils/figmaTokens";
import { egp } from "../../utils/queuePresentation";
import { countText } from "../../utils/kitPresentation";
import { cycleEndedLabel } from "../../utils/cyclePay";

// "This cycle" (044 D115), in place of Figma's "Next run" card (11355:28506): the cycle and the
// day it ended; its tutors, total and what is left to pay; a bar of the cycle's tutors by status
// with Figma's legend; and Owed now on the right (053 D5, D11), where Figma has Review all and
// Approve run — there is no approval step or scheduled run here. `summary` is cycleSummary's,
// null while the rows load or after they failed (`failed`); `owed` is { value, label }.
const SEGMENTS = [
  { key: "paid", label: "Paid", color: tokens.success.main },
  { key: "part", label: "Part paid", color: tokens.warning.main },
  { key: "unpaid", label: "Not paid", color: tokens.error.main },
];

const tutorsText = (n) => `${countText(n)} ${n === 1 ? "tutor" : "tutors"}`;

// No break inside a " · " part: each space in it becomes a no-break space, so on a phone a line
// wraps between the parts, never inside a date or a phrase ("ended 25 Sep", "E£8,650 left to
// pay") (R73). fromCharCode, not a literal: an editor can turn a literal one back into a space.
const keep = (text) => text.replace(/ /g, String.fromCharCode(0xa0));

const CycleCard = ({ cycle, summary, failed, owed }) => (
  <Box
    component="section"
    aria-labelledby="payouts-cycle-title"
    sx={{
      bgcolor: tokens.surface.default,
      // The table card's outline (kit TableCard), so the two cards read as one set.
      border: `1px solid ${tokens.border.subtle}`,
      borderRadius: "16px",
      p: { xs: "16px", sm: "20px 24px" },
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      minWidth: 0,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          flexShrink: 0,
          bgcolor: tokens.brand.subtle,
          color: tokens.icon.brand,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <RiCalendarLine size={20} aria-hidden />
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography id="payouts-cycle-title" variant="titleSection" component="h2" sx={{ color: tokens.text.primary }}>
          {keep(cycle.label)} · {keep(`ended ${cycleEndedLabel(cycle)}`)}
        </Typography>
        <Typography variant="bodySmall" component="div" sx={{ color: tokens.text.secondary }}>
          {summary ? (
            [tutorsText(summary.tutors), `${egp(summary.total)} total`, `${egp(summary.left)} left to pay`].map(keep).join(" · ")
          ) : failed ? (
            "Could not load"
          ) : (
            <Skeleton width={260} />
          )}
        </Typography>
      </Box>
      <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
        <Typography variant="titleMetric" component="div" sx={{ color: tokens.text.primary }}>
          {owed.value}
        </Typography>
        <Typography variant="bodySmall" component="div" sx={{ color: tokens.text.secondary }}>
          {owed.label}
        </Typography>
      </Box>
    </Box>
    <Box
      role="img"
      aria-label={
        summary
          ? SEGMENTS.map((s) => `${countText(summary[s.key])} ${s.label.toLowerCase()}`).join(", ")
          : failed
          ? "Not loaded"
          : "Loading"
      }
      sx={{ display: "flex", height: 8, borderRadius: "999px", overflow: "hidden", bgcolor: tokens.surface.muted }}
    >
      {summary &&
        summary.tutors > 0 &&
        SEGMENTS.map(
          (s) =>
            summary[s.key] > 0 && (
              <Box key={s.key} sx={{ width: `${(summary[s.key] / summary.tutors) * 100}%`, bgcolor: s.color }} />
            )
        )}
    </Box>
    <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
      {SEGMENTS.map((s) => (
        <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
          <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
            {s.label} · {summary ? countText(summary[s.key]) : "—"}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default CycleCard;
